import { useEffect, useState, useRef, useMemo } from "react";

import { AgGridReact } from "ag-grid-react";

import Box from "@mui/material/Box"
import { lightBlue } from '@mui/material/colors';

import { withTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { isEmpty } from "lodash";

import materialConstraintsService from "@plannel/services/mp/material-constraints-service";

import { MPItemAutocomplete } from "@plannel/components/filter/ItemAutocomplete";
import PaginationContainer from "@plannel/components/PaginationContainer";
import DefaultGridSetting from "@plannel/components/aggrid/DefaultGridSetting";
import ExcelExportButton from "@plannel/components/ExcelExportButton";
import Snackbar from "@plannel/components/Snackbar";
import Dialog from "@plannel/components/Dialog";
import DataState from "@plannel/components/aggrid/DataState";
import GridUtils from "@plannel/components/aggrid/GridUtils";
import { SaveButton } from "@plannel/components/ActionIconButton";
import debug from "@plannel/utils/debug-util";
import reduxUtil from "@plannel/utils/redux-util";
import ColorUtil from "@plannel/pages/utils/color-util";
import CodeUtil from "@plannel/pages/utils/code-util";
import FilterContainer from "@plannel/components/layout/FilterContainer";
import { MPLocationTreeFilter } from "@plannel/components/filter/LocationTreeFilter";

const { itemType } = CodeUtil;
const { snackBarMessageColor } = ColorUtil;

const MaterialConstraints = ({ t, viewName, title }) => {
  const reduxViewState = reduxUtil.getViewState(viewName);
  const reduxDispatch = useDispatch();

  const pageSizes = [100, 200, 500];
  const [currentPage, setCurrentPage] = useState(reduxViewState?.currentPage ?? 1);
  const [pageSize, setPageSize] = useState(reduxViewState?.pageSize ?? pageSizes[0]);
  const [gridPages, setGridPages] = useState(0);

  const [sortParams, setSortParams] = useState({});

  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });
  const [dialogInfo, setDialogInfo] = useState({ open: false, title: "", content: "" });

  const gridRef = useRef();
  const [gridLoading, setGridLoading] = useState(false);

  const [filterValue, setFilterValue] = useState(reduxViewState?.filter ?? { locations: null });
  const itemSearch = useRef(reduxViewState?.itemSearch ?? "");
  const extraItemCodesRef = useRef(reduxViewState?.extraItemCodes ?? []);

  const defaultGridMemo = useMemo(() => {
    return DefaultGridSetting({ title, viewName });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const [materialConstraints, setMaterialConstraints] = useState([]);

  const objectComparator = (value1, value2, property) => {
    const val1 = !value1 ? "" : value1[property];
    const val2 = !value2 ? "" : value2[property];
    if (val1 === val2) {
      return 0;
    } else if (val1 > val2) {
      return 1;
    }
    return -1;
  }
  
  const valueFormatter = (param) => {
    if (param.column?.colDef) {
      const { colId } = param.column.getColDef();
      if (colId === "locCd") {
        return param.value?.locCd ? param.value.locCd : "";
      } else if (colId === "locName") {
        return param.value?.name ? param.value.name : "";
      } else if (colId === "itemCd") {
        return param.value?.itemCd ? param.value.itemCd : "";
      } else if (colId === "itemName") {
        return param.value?.name ? param.value.name : "";
      } else if (colId === "itemType") {
        return param.value?.itemType ? param.value.itemType : "";
      } else if (colId === "supplierCd") {
        return param.value?.supplierCd ? param.value.supplierCd : "";
      } else if (colId === "supplierName") {
        return param.value?.supplier?.name ? param.value?.supplier?.name : "";
      } else if (colId === "avgLtDays") {
        return param.value?.avgLtDays ? param.value.avgLtDays : "";
      }
    }
    return param.value?.name ? param.value.name : "";
  }


  const supplierNameRenderer = (params) => {
    return params.value?.suppliers?.name ? params.value?.suppliers?.name : "";
  }

  const itemValueFormatter = params => {
    const { value } = params;

    return itemType?.[value?.itemType] ? GridUtils.gridValueL10N(itemType[value?.itemType]?.name) : value;
  };
  
  const columnDefs = [
    {
      headerName: "locCd", field: "location", colId: "locCd",
      type: ['nonEditableColumn'],
      valueFormatter,
      comparator: (value1, value2) => objectComparator(value1, value2, "locCd"),
      width: 200, cellClass: 'stringType'
    },
    {
      headerName: "locName", field: "location", colId: "locName",
      type: ["nonEditableColumn"],
      valueFormatter,
      comparator: (value1, value2) => objectComparator(value1, value2, "name"),
      width: 200
    },
    {
      headerName: "itemCd", field: "item", colId: "itemCd",
      type: ["nonEditableColumn"],
      valueFormatter,
      comparator: (value1, value2) => objectComparator(value1, value2, "itemCd"),
      width: 200, cellClass: 'stringType'
    },
    {
      headerName: "itemName", field: "item", colId: "itemName",
      type: ["nonEditableColumn"],
      valueFormatter,
      comparator: (value1, value2) => objectComparator(value1, value2, "name"),
      width: 200
    },
    {
      headerName: "itemType", field: "item", colId: "itemType",
      type: ["nonEditableColumn"],
      valueFormatter: itemValueFormatter,
      comparator: (value1, value2) => objectComparator(value1, value2, "type"),
      width: 200
    },
    {
      headerName: "supplierCd", field: "supplierItem.supplier", colId: "supplierCd",
      type: ["nonEditableColumn"],
      valueFormatter,
      comparator: (value1, value2) => objectComparator(value1, value2, "supplierName"),
      width: 200, cellClass: 'stringType'
    },
    {
      headerName: "supplierName", field: "supplierItem", colId: "supplierName",
      cellEditor: "agRichSelectCellEditor",
      cellEditorPopup: true,
      valueFormatter,
      cellStyle: {
        background: lightBlue[50]
      },
      cellEditorParams: params => {
        return {
          valueFormatter: supplierNameRenderer,
          values: params.data.supplierItemList ? params.data.supplierItemList  : [],
          valuePlaceholder: ''
        };
      },
      comparator: (value1, value2) => objectComparator(value1, value2, "name"),
      width: 200
    },
    {
      headerName: "avgLtDays", field: "supplierItem", colId: "avgLtDays", type: ["numberColumn", "rightAligned", "nonEditableColumn"],
      valueFormatter,
      comparator: (value1, value2) => objectComparator(value1, value2, "avgLtDays"),
      width: 200
    },
    { headerName: "currentStkQ", field: "invQty", type: ["numberColumn", "nonEditableColumn", "rightAligned"], width: 170 },
    { headerName: "mcFlg", field: "mcFlg", type: ["booleanColumn"],
      cellStyle: {
        background: lightBlue[50]
      },
      width: 120
    }
  ];

  const getRequestParams = (isPagination = true) => {
    const params = {};

    if (filterValue?.locations) {
      params.locCd = filterValue?.locations?.map(location => location?.locCd);
    }

    if (extraItemCodesRef.current?.length > 0) {
      params.items = [itemSearch.current, ...extraItemCodesRef.current].filter(Boolean);
    } else if (itemSearch.current) {
      params.item = itemSearch.current;
    }

    if (isPagination) {
      if (currentPage) {
        params.page = currentPage - 1;
      }

      if (pageSize) {
        params.pageSize = pageSize;
      }
    }

    params.nowDate = new ZDate();

    return { ...params, ...sortParams };
  }

  const retrieveMaterialConstraints = () => {
    const params = getRequestParams();

    materialConstraintsService.getAll(params)
      .then(response => {
        const { results, totalPages } = response.data;
        if (isEmpty(results)) {
          setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoData") });
        }

        setMaterialConstraints(results);
        setGridPages(totalPages);
      })
      .catch(e => {
        debug(e);
      })
      .then(() => {
        const reduxState = reduxUtil.createObjectState(viewName, { itemSearch: itemSearch.current, extraItemCodes: extraItemCodesRef.current, currentPage, pageSize });
        reduxDispatch(reduxUtil.updateViewState(reduxState));
        DataState.initialize(gridRef.current?.api);
      });
  }

  const saveMaterialConstraints = () => {
    gridRef.current.api.stopEditing(false);
    const allStateData = DataState.getAllStateData(gridRef.current.api);

    if (allStateData.length > 0) {
      materialConstraintsService.update(allStateData)
        .then(response => {
          const { count, message } = response.data;
          if (message) {
            setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t(message) });
          }
          if (count > 0) {
            setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_SaveSuccess") });
          }
        })
        .catch(e => {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_SaveFailed") });
          debug(e);
        })
        .then(() => {
          retrieveMaterialConstraints();
        });
    } else {
      setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t("MSG_NoSaveData") });
    }
  };

  const handleDialog = (action) => {
    if (action === "SAVE") {
      saveMaterialConstraints();
    }
  }

  const initPageToRetrieve = () => {
    if (currentPage > 1) {
      setCurrentPage(1);
    } else {
      retrieveMaterialConstraints();
    }
  }

  useEffect(() => {
    if (gridLoading) {
      DataState.initialize(gridRef.current?.api);
      retrieveMaterialConstraints();
    }

    return () => {
      setMaterialConstraints([]);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, gridLoading, sortParams]);

  useEffect(() => {
    if (gridLoading) {
      initPageToRetrieve();
    }

    return () => {
      setMaterialConstraints([]);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterValue]);

  const onGridReady = params => {
    setGridLoading(true);

    GridUtils.setColumnDefs({ ...params, columnDefs, viewName, initState: true });
  }

  return (
    <>
      <FilterContainer>
        <MPLocationTreeFilter viewName={viewName} filterValue={filterValue?.locations?.map(location => location?.locCd)} onChangeFilter={setFilterValue} />
        <MPItemAutocomplete retrieve={initPageToRetrieve} searchValue={itemSearch} key={"ITEM"} extraCodesRef={extraItemCodesRef} />
        <Box sx={{ ml: "auto", mr: 1.5 }}>
          <ExcelExportButton title={title} service={materialConstraintsService}  columnDefs={columnDefs} params={getRequestParams(false)} />
          <SaveButton onClick={() => setDialogInfo({ open: true, title: "save", content: t("MSG_SaveConfirm") })} />
        </Box>
      </FilterContainer>
      <Box id="material-constraints" className="ag-theme-balham grid-area" mb={1}>
        <AgGridReact
          ref={gridRef}
          rowData={materialConstraints}
          {...defaultGridMemo}
          onGridReady={onGridReady}
          onSortChanged={params => setSortParams(GridUtils.getSortState(params))}
        />
      </Box>
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
      <Dialog
        open={dialogInfo.open}
        onClose={() => setDialogInfo({ open: false, title: "", content: "" })}
        title={dialogInfo.title}
        actionName={dialogInfo.actionName}
        content={dialogInfo.content}
        onHandler={handleDialog}
      />
      <PaginationContainer
        currentPage={currentPage} totalPages={gridPages}
        pageSize={pageSize} pageSizes={pageSizes}
        setCurrentPage={setCurrentPage} setPageSize={setPageSize}
      />
    </>
  );
};

export default withTranslation()(MaterialConstraints);